import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { map } from 'lodash';
import { Chip } from '@mui/material';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { chipStyles, formatPercent, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import {
  fetchCustomerAttributions,
  getCustomerAttributionSubject,
  getCustomerAttributionLoadingSubject,
  CustomerAttribution
} from '../../../store/CustomerAttribution';

const financialColumns = {
  chargeRecurringExpected: 'Expected MRC',
  chargeRecurringActual: 'Actual MRC',
  revenueRecurringExpected: 'Expected MRR',
  revenueRecurringActual: 'Actual MRR',
  revenueRecurringNetActual: 'Net MRR',
  revenueOnetimeExpected: 'Expected NRR',
  revenueOnetimeActual: 'Actual NRR',
  revenueOnetimeNetActual: 'Net NRR',
  revenueTrueupExpected: 'Expected True-Up',
  revenueTrueupActual: 'Actual True-Up',
  revenueSpiffActual: 'SPIFF',
  revenueRecurringCorrection: 'MRR Correction',
  revenueOnetimeCorrection: 'NRR Correction'
};

export const customerAttributionGridCols: GridColDef[] = [
  {
    field: 'runDate',
    headerName: 'Run',
    headerAlign: 'left',
    align: 'left',
    flex: 0.75,
    minWidth: 100,
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams<CustomerAttribution>) => {
      const date = params.value as string;
      const status = params.row.runStatus;
      if (!date) return '';
      const formattedDate = getFormattedDate(date, 'MMM YY');
      return (
        <Chip
          label={formattedDate}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    }
  },
  ...map(
    Object.keys(financialColumns),
    (key) =>
      ({
        headerName: financialColumns[key as keyof typeof financialColumns],
        field: key,
        renderCell: (params: GridCellParams<CustomerAttribution>) => {
          const value = params.value as number | undefined;
          const isPercent = key.toLowerCase().includes('percent');
          if ((value === undefined || value === null) && isPercent) return '';
          if (isPercent) {
            return (
              <span className={`float-right ${Number(value) < 0 ? 'text-price-red' : ''}`}>
                {formatPercent(value, true)}
              </span>
            );
          }
          return (
            <span className={`float-right ${value && Number(value) < 0 ? 'text-price-red' : ''}`}>
              {formatPrice((value as number) || 0)}
            </span>
          );
        },
        flex: 1,
        minWidth: 120,
        filterable: false,
        sortable: false,
        headerAlign: 'right',
        headerClassName: key.includes('NetActual') ? '!bg-azure-200' : undefined,
        cellClassName: key.includes('NetActual') ? '!bg-azure-100' : undefined
      }) as GridColDef
  )
];

interface CustomerAttributionTableProps {
  customerId: string;
  supplierId?: string;
}

export const CustomerAttributionTable = ({ customerId, supplierId }: CustomerAttributionTableProps) => {
  const [records, setRecords] = useState<CustomerAttribution[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchCustomerAttributions(customerId, supplierId);

    const attributionSub = getCustomerAttributionSubject().subscribe((data) => {
      if (data) {
        setRecords(data.attributions);
      }
    });

    const loadingSub = getCustomerAttributionLoadingSubject().subscribe((isLoading) => {
      setLoading(isLoading);
    });

    return () => {
      if (attributionSub) attributionSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [customerId, supplierId]);

  return (
    <div
      className="w-full"
      data-testid="customer-attribution-table-container">
      {loading && <ComponentSpinner label="Loading Data" />}
      {!loading && (
        <BasicDataGrid
          rows={records}
          columns={customerAttributionGridCols}
          sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
          disableColumnMenu
          showColumnVerticalBorder
          showCellVerticalBorder
          dataTestId="customer-attribution-table"
          pinnedColumns={{ left: ['runDate'], right: [] }}
        />
      )}
    </div>
  );
};
